import { AlertProps, AlertType } from "@ppb/the-wall-common/types";
import { currencyFormatWithDecimalPlaces } from "../formatters/currency-adapter";
import type { BetslipLocalization, CountryCode, Jurisdiction } from "../contract";
import { i18n } from "../helpers/i18n";
import { StaticKey, TranslationKey } from "../translations/keys";
import { NotificationCode } from "../components/betslip-notification-code";

const UNSUPPORTED_MAX_PAYOUT_COUNTRIES: CountryCode[] = ["IE", "GB"];
const UNSUPPORTED_MAX_PAYOUT_JURISDICTIONS: Jurisdiction[] = ["ITALY", "BRAZIL"];

const getTranslationKey = (translationKey: StaticKey, jurisdiction: Jurisdiction | null): TranslationKey => {
  switch (jurisdiction) {
    case "BRAZIL":
      return `${translationKey}.BRAZIL`;
    default:
      return translationKey;
  }
};

export const GTM_NOTIFICATIONS: Array<string> = [
  NotificationCode.AboveMaxStake,
  NotificationCode.BelowMinStake,
  NotificationCode.AboveMaxPayout,
  NotificationCode.IncrementOutOfRage,
  NotificationCode.InsufficientFunds,
  NotificationCode.MaxPayoutDailyLimit,
  NotificationCode.MaxPayoutInfo,
];

export const isMaxPayoutNotificationSupported = ({ countryCode, jurisdiction }: BetslipLocalization): boolean =>
  !(
    UNSUPPORTED_MAX_PAYOUT_COUNTRIES.includes(countryCode) ||
    (!!jurisdiction && UNSUPPORTED_MAX_PAYOUT_JURISDICTIONS.includes(jurisdiction))
  );

export const getBelowMinStakeNotification = (minValue: number, localization: BetslipLocalization): AlertProps => ({
  type: AlertType.Error,
  items: [
    i18n({
      key: "I18N.BETSLIP.VALIDATION_BELOW_MIN_STAKE",
      interpolationValues: {
        minStake: `${currencyFormatWithDecimalPlaces({
          ...localization,
          value: minValue,
          decimalPlaces: 2,
        })}`,
      },
    }),
  ],
  detail: i18n({ key: "I18N.BETSLIP.TAP_TO_UPDATE" }),
  hasClickableAction: true,
  gtmLabel: `${AlertType.Error}${getTranslationKey(
    "I18N.BETSLIP.VALIDATION_BELOW_MIN_STAKE",
    localization.jurisdiction,
  )}`,
});

export const getAboveMaxStakeNotification = (maxValue: number, localization: BetslipLocalization): AlertProps => ({
  type: AlertType.Error,
  items: [
    i18n({
      key: "I18N.BETSLIP.VALIDATION_ABOVE_MAX_STAKE",
      interpolationValues: {
        maxStake: `${currencyFormatWithDecimalPlaces({
          ...localization,
          value: maxValue,
          decimalPlaces: 2,
        })}`,
      },
    }),
  ],
  detail: i18n({ key: "I18N.BETSLIP.TAP_TO_UPDATE" }),
  hasClickableAction: true,
  gtmLabel: `${AlertType.Error}${getTranslationKey(
    "I18N.BETSLIP.VALIDATION_ABOVE_MAX_STAKE",
    localization.jurisdiction,
  )}`,
});

export const getAboveMaxPayoutNotification = (maxValue: number, localization: BetslipLocalization): AlertProps => ({
  type: AlertType.Error,
  items: [
    i18n({
      key: "I18N.BETSLIP.VALIDATION_ABOVE_MAX_PAYOUT",
      interpolationValues: {
        maxPayout: `${currencyFormatWithDecimalPlaces({
          ...localization,
          value: maxValue,
          decimalPlaces: 2,
        })}`,
      },
    }),
  ],
  gtmLabel: `${AlertType.Error}${getTranslationKey(
    "I18N.BETSLIP.VALIDATION_ABOVE_MAX_PAYOUT",
    localization.jurisdiction,
  )}`,
});

export const getIncrementOutOfRangeNotification = (
  minStakeIncrement: number,
  localization: BetslipLocalization,
): AlertProps => ({
  type: AlertType.Error,
  items: [
    i18n({
      key: "I18N.BETSLIP.VALIDATION_INCREMENT_OUT_OF_RANGE",
      interpolationValues: {
        increment: `${currencyFormatWithDecimalPlaces({
          ...localization,
          value: minStakeIncrement,
          decimalPlaces: 2,
        })}`,
      },
    }),
  ],
  gtmLabel: `${AlertType.Error}${getTranslationKey(
    "I18N.BETSLIP.VALIDATION_INCREMENT_OUT_OF_RANGE",
    localization.jurisdiction,
  )}`,
});

export const getAboveMaxPayoutGroupErrorNotification = (
  localization: BetslipLocalization,
  maxPayoutKey: TranslationKey,
): AlertProps => ({
  id: NotificationCode.MaxPayoutDailyLimit,
  type: AlertType.Error,
  items: [
    i18n({
      key: getTranslationKey("I18N.BETSLIP.VALIDATION_GROUP_ABOVE_MAX_PAYOUT", localization.jurisdiction),
      interpolationValues: {
        maxPayout: i18n({
          key: maxPayoutKey,
        }),
      },
    }),
  ],
  detail: i18n({
    key: "I18N.BETSLIP.VALIDATION_PLEASE_REVIEW_YOUR_STAKE",
  }),
  gtmLabel: `${AlertType.Error}${getTranslationKey(
    "I18N.BETSLIP.VALIDATION_GROUP_ABOVE_MAX_PAYOUT",
    localization.jurisdiction,
  )}`,
});

export const getAboveMaxPayoutGroupWarningNotification = (
  localization: BetslipLocalization,
  maxPayoutKey: TranslationKey,
  maxPayoutTermsUrl: string,
): AlertProps => ({
  id: NotificationCode.MaxPayoutDailyLimit,
  type: AlertType.Warning,
  items: [
    i18n({
      key: getTranslationKey("I18N.BETSLIP.VALIDATION_SPORT_MAX_PAYOUT_TITLE", localization.jurisdiction),
    }),
  ],
  detail: i18n({
    key: getTranslationKey("I18N.BETSLIP.VALIDATION_MAX_DAILY_PAYOUT_WARNING", localization.jurisdiction),
    interpolationValues: {
      maxPayout: i18n({
        key: maxPayoutKey,
      }),
    },
  }),
  extraDetailInfo: i18n({
    key: "I18N.BETSLIP.VALIDATION_SEE_TC_SHORT",
  }),
  url: maxPayoutTermsUrl,
  gtmLabel: `${AlertType.Warning}${getTranslationKey(
    "I18N.BETSLIP.VALIDATION_GROUP_ABOVE_MAX_PAYOUT",
    localization.jurisdiction,
  )}`,
});

export const getMaxPayoutNotification = (maxPayoutTermsUrl: string): AlertProps => ({
  id: NotificationCode.MaxPayoutInfo,
  type: AlertType.Warning,
  message: i18n({ key: "I18N.BETSLIP.GENERIC.PAYOUT.HEADER" }),
  detail: i18n({ key: "I18N.BETSLIP.GENERIC.PAYOUT.DESCRIPTION" }),
  extraDetailInfo: i18n({ key: "I18N.BETSLIP.VALIDATION_SEE_TC_SHORT" }),
  url: maxPayoutTermsUrl,
  dismissLabel: i18n({ key: "I18N.BETSLIP.MAX.PAYOUT.ACCEPT" }),
  showCloseIcon: false,
});
